import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import * as API from '../../../../api/pocos';
import CapturarFoto, { FotoLocal } from '../../../form/CapturarFoto';
import Arquivos from '../components/Arquivos';
import Campos, { campo, opcional } from '../components/Campos';
import Editor from '../components/Editor';
import ResumoMedicao from '../components/ResumoMedicao';
import { hoje, mensagemErro, nomeMedidor, payloadMedicao } from '../modules/valores';

export default function MedicaoForm({ original = {}, bem, medidores, readOnly, onClose, onSaved }) {
    const nomePoco = bem.pocoIdentificacao || bem.descricao || 'Poço';
    const localPoco = [bem.logradouro, bem.numero, bem.complemento, bem.bairro].filter(Boolean).join(' • ');
    const [conferindo, setConferindo] = useState(false);
    const [ultimas, setUltimas] = useState({});
    const [erroHistorico, setErroHistorico] = useState('');
    const gravandoRef = useRef(false),
        chaveRef = useRef(null);
    const medidoresDoPoco = medidores.filter((m) => Number(m.produtoBensId) === Number(bem.id));
    const [dados, setDados] = useState(() => ({
        dataMedicao: hoje(),
        ...original,
        produtoBensId: bem.id,
        informarVolume: ['INFORMADO', 'IMPORTADO'].includes(original.origemVolume),
        informarEnergia: ['INFORMADO', 'IMPORTADO'].includes(original.origemEnergia),
    }));
    const [lacres, setLacres] = useState([]),
        [erro, setErro] = useState('');
    const [fotos, setFotos] = useState([]),
        [enviando, setEnviando] = useState(false);
    const [salvo, setSalvo] = useState(null),
        [falhaFoto, setFalhaFoto] = useState(false);
    const resultado = useRef(null),
        resumoRef = useRef(null);
    useEffect(() => {
        if (salvo) {
            resumoRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
            resumoRef.current?.focus();
        }
    }, [salvo]);
    function adicionar(files, captura = false) {
        setConferindo(false);
        const novas = [...fotos, ...files];
        if (novas.length > 5 || novas.some((f) => f.size > 10 * 1024 * 1024 || !['image/jpeg', 'image/png'].includes(f.type))) {
            setErro('Selecione até 5 fotos JPEG ou PNG, de até 10 MB cada. A seleção anterior foi mantida.');
            return;
        }
        setErro('');
        setFotos(novas);
        if (captura && !original.uuid) {
            const agora = new Date(),
                dois = (n) => String(n).padStart(2, '0');
            setDados((prev) => ({
                ...prev,
                dataMedicao:
                    prev.dataMedicao || [agora.getFullYear(), dois(agora.getMonth() + 1), dois(agora.getDate())].join('-'),
                horaMedicao:
                    prev.horaMedicao || [dois(agora.getHours()), dois(agora.getMinutes()), dois(agora.getSeconds())].join(':'),
            }));
        }
    }
    async function salvar() {
        if (resultado.current) {
            onSaved(resultado.current);
            return;
        }
        if (gravandoRef.current) return;
        if (
            Number(dados.produtoBensId) !== Number(bem.id) ||
            (original.produtoBensId != null && Number(original.produtoBensId) !== Number(bem.id))
        )
            throw new Error('O poço do formulário mudou. Feche e abra a medição novamente.');
        const payload = {
            ...payloadMedicao(dados, original),
            produtoBensId: bem.id,
            justificativaRepeticao: dados.justificativaRepeticao || '',
        };
        if (!conferindo) {
            setConferindo(true);
            resumoRef.current?.scrollIntoView?.({ block: 'start' });
            return;
        }
        if (!original.uuid && !chaveRef.current) {
            const bytes = new Uint8Array(16);
            window.crypto.getRandomValues(bytes);
            chaveRef.current = Array.from(bytes, (n) => n.toString(16).padStart(2, '0')).join('');
        }
        if (!original.uuid) payload.chaveRequisicao = chaveRef.current;
        gravandoRef.current = true;
        setEnviando(true);
        try {
            const response = await API.gravar(`medicoes/${original.uuid ? 'editar' : 'cadastrar'}`, payload);
            resultado.current = response;
            setSalvo(response.dados);
            if (fotos.length) {
                try {
                    await API.anexar(
                        { pocoMedicaoId: response.dados.id },
                        'FOTO_LEITURA',
                        fotos,
                        'Fotos anexadas no formulário da medição.'
                    );
                } catch (e) {
                    setFalhaFoto(true);
                    throw new Error(
                        'A medição foi salva, mas o envio das fotos não foi confirmado. Confira os anexos abaixo antes de reenviar pelo painel. ' +
                            mensagemErro(e)
                    );
                }
            }
            // Mantém o resultado aberto. Concluir/Fechar comunica a gravação à lista sem salvar novamente.
        } catch (e) {
            setConferindo(false);
            throw e;
        } finally {
            gravandoRef.current = false;
            setEnviando(false);
        }
    }
    const medidor = medidoresDoPoco.find((m) => Number(m.id) === Number(dados.medidorAguaId));
    useEffect(() => {
        const controller = new AbortController();
        setLacres([]);
        setErro('');
        if (medidor)
            API.consultar('lacres/listar', { medidorUuid: medidor.uuid }, { signal: controller.signal })
                .then((r) => {
                    if (!controller.signal.aborted) setLacres(r.dados);
                })
                .catch((e) => {
                    if (!controller.signal.aborted) setErro(mensagemErro(e));
                });
        return () => controller.abort();
    }, [medidor]);
    useEffect(() => {
        const controller = new AbortController();
        setUltimas({});
        setErroHistorico('');
        if (dados.medidorAguaId || dados.medidorEnergiaId)
            API.opcoes('medicoes', bem.id, controller.signal)
                .then((rows) => {
                    if (controller.signal.aborted) return;
                    const ativas = rows
                        .filter((r) => r.ativo !== false && r.ativo !== 0 && r.situacao !== 'CANCELADA')
                        .sort(
                            (a, b) =>
                                String(b.dataMedicao).localeCompare(String(a.dataMedicao)) ||
                                String(b.horaMedicao || '').localeCompare(String(a.horaMedicao || '')) ||
                                Number(b.id) - Number(a.id)
                        );
                    setUltimas(
                        Object.fromEntries(
                            ['Agua', 'Energia'].map((tipo) => [
                                tipo,
                                ativas.find(
                                    (r) =>
                                        Number(r.produtoBensId) === Number(bem.id) &&
                                        dados[`medidor${tipo}Id`] &&
                                        Number(r[`medidor${tipo}Id`]) === Number(dados[`medidor${tipo}Id`]) &&
                                        r[`leitura${tipo}`] != null
                                ) || null,
                            ])
                        )
                    );
                })
                .catch((e) => {
                    if (!controller.signal.aborted)
                        setErroHistorico('Não foi possível consultar a última leitura: ' + mensagemErro(e));
                });
        return () => controller.abort();
    }, [bem.id, dados.medidorAguaId, dados.medidorEnergiaId]);
    function change(updater) {
        setConferindo(false);
        setDados((prev) => {
            const next = updater(prev);
            return String(next.medidorAguaId ?? '') !== String(prev.medidorAguaId ?? '') ? { ...next, lacreAguaId: '' } : next;
        });
    }
    const meters = (tipo) =>
        opcional(medidoresDoPoco.filter((m) => m.tipo === tipo).map((m) => ({ value: m.id, label: nomeMedidor(m) })));
    const sealOptions = opcional(lacres.map((l) => ({ value: l.id, label: `${l.numero}${l.retiradoEm ? ' • retirado' : ''}` })));
    if (dados.lacreAguaId && !lacres.some((l) => Number(l.id) === Number(dados.lacreAguaId)))
        sealOptions.push({ value: dados.lacreAguaId, label: original.lacreAguaNumero || `Lacre ${dados.lacreAguaId}` });
    return (
        <Editor
            title={`${original.uuid ? 'Medição / retificação' : 'Nova medição'} — ${nomePoco}`}
            readOnly={readOnly}
            onClose={() => (resultado.current ? onSaved(resultado.current) : onClose())}
            label={salvo ? 'Concluir' : conferindo ? `Confirmar e salvar — ${nomePoco}` : `Conferir medição — ${nomePoco}`}
            onSave={salvar}
        >
            <Box
                component="details"
                sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, '&[open]': { pb: 1 } }}
            >
                <Box
                    component="summary"
                    title="Abrir ou recolher a localização, os medidores e as últimas leituras deste poço"
                    sx={{
                        cursor: 'pointer',
                        py: 1.25,
                        minHeight: 44,
                        boxSizing: 'border-box',
                        color: 'primary.main',
                        fontWeight: 600,
                        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                    }}
                >
                    Detalhes do poço
                </Box>
                <Typography variant="body2">{localPoco || 'Localização não preenchida no cadastro do poço.'}</Typography>
                {['Agua', 'Energia'].map((tipo) => {
                    const m = medidoresDoPoco.find((x) => Number(x.id) === Number(dados[`medidor${tipo}Id`]));
                    const ultima = ultimas[tipo];
                    return (
                        <Box key={tipo} sx={{ mt: 0.5 }}>
                            <Typography variant="body2">
                                {tipo === 'Agua' ? 'Hidrômetro' : 'Energia'}: {m ? nomeMedidor(m) : 'equipamento não selecionado'}
                            </Typography>
                            <Typography variant="caption">
                                Último registro disponível:{' '}
                                {ultima
                                    ? `${ultima[`leitura${tipo}`] ?? 'sem leitura'} ${tipo === 'Agua' ? 'm³' : 'kWh'} • ${ultima.dataMedicao} ${ultima.horaMedicao || ''}`
                                    : 'não disponível'}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
            {erroHistorico && <Alert severity="warning">{erroHistorico}</Alert>}
            {conferindo && !salvo && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography fontWeight="bold">Confira antes de salvar no {nomePoco}</Typography>
                    {['Agua', 'Energia'].map((tipo) => {
                        const equipamento = medidoresDoPoco.find((m) => Number(m.id) === Number(dados[`medidor${tipo}Id`]));
                        return (
                            <Typography key={tipo} sx={{ overflowWrap: 'anywhere' }}>
                                {tipo === 'Agua' ? 'Hidrômetro' : 'Medidor de energia'}:{' '}
                                {equipamento ? nomeMedidor(equipamento) : 'equipamento não selecionado'}
                            </Typography>
                        );
                    })}
                    <Typography>
                        Data: {dados.dataMedicao} • Hora: {dados.horaMedicao || 'não informada'}
                    </Typography>
                    <Typography>
                        Água: {dados.leituraAgua ?? 'não informada'} m³ • Energia: {dados.leituraEnergia ?? 'não informada'} kWh
                    </Typography>
                    <Typography>Bombeamento: {dados.bombeamentoMinutos ?? 'não informado'} min</Typography>
                    {dados.informarVolume && <Typography>Volume manual: {dados.volumeExtraidoM3} m³</Typography>}
                    {dados.informarEnergia && <Typography>Consumo manual: {dados.energiaConsumidaKwh} kWh</Typography>}
                    <Typography>Confirme o nome do poço, a série dos medidores e os números das fotos.</Typography>
                    <Button
                        type="button"
                        disabled={enviando}
                        onClick={() => setConferindo(false)}
                        title="Voltar aos campos antes de gravar"
                    >
                        Corrigir dados
                    </Button>
                </Alert>
            )}
            <Alert severity="info">
                {original.uuid
                    ? 'A edição mantendo a data preserva o vínculo atual. Para associar uma outorga, use Regularizar outorga na lista.'
                    : 'A outorga será selecionada pela data. Ausência, vencimento ou sobreposição geram pendência e permitem salvar a medição.'}
            </Alert>
            <Alert severity="info" sx={{ mt: 1 }}>
                Copie o valor acumulado dos visores de água e energia. Informe em bombeamento apenas os minutos adicionais
                trabalhados naquele dia, sem repetir minutos já lançados. Não informe o número acumulado do horímetro.
            </Alert>
            {(salvo || original.id) && (
                <Box ref={resumoRef} tabIndex={-1}>
                    <ResumoMedicao registro={salvo || original} salvo={Boolean(salvo)} />
                </Box>
            )}
            {original.pendenciaOutorga && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    Regularização pendente: {original.pendenciaOutorga.replaceAll('_', ' ')}.
                </Alert>
            )}
            {erro && <Alert severity="error">{erro}</Alert>}
            <Campos
                dados={dados}
                setDados={change}
                disabled={readOnly || enviando || Boolean(salvo) || conferindo}
                fields={[
                    campo('dataMedicao', 'Data da medição', 'date', { required: true }),
                    campo('horaMedicao', 'Hora da medição', 'time', { inputProps: { step: 1 } }),
                    campo('medidorAguaId', 'Hidrômetro', 'text', { options: meters('AGUA') }),
                    campo('lacreAguaId', 'Lacre do hidrômetro', 'text', { options: sealOptions }),
                    campo('leituraAgua', 'Leitura acumulada da água (m³)', 'decimal', {
                        title: 'Copie o número acumulado do hidrômetro em m³. O consumo será calculado usando a leitura anterior do mesmo equipamento.',
                        help: 'Número do visor; não é o consumo do dia.',
                    }),
                    campo('medidorEnergiaId', 'Medidor de energia', 'text', { options: meters('ENERGIA') }),
                    campo('leituraEnergia', 'Leitura acumulada de energia (kWh)', 'decimal', {
                        title: 'Copie o registro de energia acumulada em kWh. Não use potência (kW), tensão (V) ou corrente (A).',
                        help: 'Use o visor em kWh; confirme o registro exibido no equipamento.',
                    }),
                    campo('bombeamentoMinutos', 'Bombeamento adicional neste dia (minutos)', 'number', {
                        inputProps: { min: 0, max: 1440, step: 1 },
                        title: 'Minutos de funcionamento ainda não registrados nesta data. O sistema soma todos os registros do dia vinculados à mesma outorga.',
                        help: 'Exemplo: 2 horas = 120 minutos. Não repetir o total do dia nem copiar o horímetro acumulado.',
                    }),
                    campo('informarVolume', 'Informar volume manualmente', 'boolean'),
                    campo('informarEnergia', 'Informar consumo de energia manualmente', 'boolean'),
                    ...(dados.informarVolume
                        ? [campo('volumeExtraidoM3', 'Volume informado (m³)', 'decimal', { required: true })]
                        : []),
                    ...(dados.informarEnergia
                        ? [campo('energiaConsumidaKwh', 'Consumo de energia informado (kWh)', 'decimal', { required: true })]
                        : []),
                    ...(dados.informarVolume || dados.informarEnergia
                        ? [
                              campo('intervaloInicio', 'Início do intervalo', 'date'),
                              campo('intervaloFim', 'Fim do intervalo', 'date'),
                          ]
                        : []),
                    campo('justificativaRepeticao', 'Justificativa se a leitura já foi registrada', 'text', {
                        full: true,
                        multiline: true,
                        title: 'Preencha somente se o sistema avisar que a leitura se repete. Explique por que o contador não avançou.',
                        help: 'Leitura repetida legítima exige pelo menos 10 caracteres de justificativa; duplicata no mesmo instante permanece bloqueada.',
                    }),
                    campo('observacao', 'Observação', 'text', { full: true, multiline: true, minRows: 2 }),
                    ...(original.uuid ? [campo('motivo', 'Motivo da retificação', 'text', { required: true, full: true })] : []),
                ]}
            />
            {!readOnly && !salvo && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1">Fotos da leitura e do lacre</Typography>
                    <Typography variant="body2">
                        Fotografe a contagem e o lacre com nitidez. As fotos serão enviadas ao salvar a medição.
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" spacing={1}>
                        <CapturarFoto disabled={enviando} nome="medicao" onCapture={(file) => adicionar([file], true)} />
                        <Button
                            component="label"
                            disabled={enviando}
                            title="Adicionar até cinco fotos JPEG ou PNG, com até 10 MB cada"
                        >
                            Selecionar fotos
                            <input
                                hidden
                                type="file"
                                multiple
                                accept="image/jpeg,image/png"
                                disabled={enviando}
                                onChange={(e) => {
                                    adicionar(Array.from(e.target.files || []));
                                    e.target.value = '';
                                }}
                            />
                        </Button>
                    </Stack>
                    <Typography variant="caption">
                        Confira data e hora da medição. Em uma nova medição, ao aceitar a captura, a hora vazia recebe o horário
                        atual do dispositivo. Fotos de arquivo não alteram o horário.
                    </Typography>
                </Box>
            )}
            {!!fotos.length && (
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                    {fotos.map((file, i) => (
                        <Box key={i} sx={{ width: 170 }}>
                            <FotoLocal arquivo={file} sx={{ width: '100%', height: 120, objectFit: 'contain' }} />
                            <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
                                {file.name}
                            </Typography>
                            {!salvo && (
                                <Button
                                    type="button"
                                    disabled={enviando}
                                    title="Remover foto ainda não enviada"
                                    onClick={() => setFotos((prev) => prev.filter((_, n) => n !== i))}
                                >
                                    Remover
                                </Button>
                            )}
                        </Box>
                    ))}
                </Stack>
            )}
            {falhaFoto && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    A medição já está salva. As miniaturas acima são a seleção local, não a confirmação do envio. Confira a lista
                    abaixo; se faltarem fotos, selecione-as novamente e envie pelo painel. Concluir fecha o formulário sem repetir
                    a gravação nem reenviar automaticamente.
                </Alert>
            )}
            {(salvo || original.id) && (
                <Arquivos
                    key={salvo ? 'salvo' : 'original'}
                    tipo="medicoes"
                    registro={salvo || original}
                    podeAnexar={Boolean(salvo) && !readOnly && salvo.situacao !== 'CANCELADA'}
                />
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>
                As fotos também continuam disponíveis no painel expandido da grid.
            </Typography>
        </Editor>
    );
}
